<template>
  <!-- The desk: the signed-in home. The system is the straight man; the
       assistant's own words carry the personality (riso NOTES §6). -->
  <main class="page desk-page">
    <p v-if="loading" class="lab">Reading the desk…</p>

    <template v-else>
      <header class="top">
        <div>
          <span class="lab">{{ todayLabel }}</span>
          <h1 class="d2 head">{{ headline }}</h1>
        </div>
        <span v-if="mode === 'shadow'" class="rstamp held">Held at the counter</span>
        <span v-else-if="mode === 'live'" class="rstamp live">Sending</span>
      </header>

      <div v-if="latest" class="said">
        <p class="lede">{{ latest.text }}</p>
        <span class="lab sig">— the desk · {{ formatDate(latest.at) }}</span>
      </div>

      <!-- The rail: say what mode we're in, honestly, and what it means. -->
      <div class="panel kraftp rail">
        <!-- Shadow holds only the new brain. For an active member the older
             scheduler still runs (agentOrchestrator: runLegacy = runtime !== "live")
             and its postcards are real, so a member is never told nothing leaves. -->
        <p v-if="mode === 'shadow' && isMember" class="sm">
          <strong>Two of us work this desk for now.</strong> The older scheduler still posts the routine cards on its own &mdash;
          those are real, and each one is on that person&#8217;s file. I read everyone every morning, decide and draft, and what I
          would have sent is held below, so you can see whether I&#8217;m any good before I&#8217;m trusted with a stamp. Switching
          me on happens from our side, not from this page, for now.
        </p>
        <p v-else-if="mode === 'shadow'" class="sm">
          <strong v-if="neverSent">Day {{ daysRunning }} of writing nothing into the void.</strong>
          <strong v-else>Shadow mode.</strong> I read everyone every morning, decide, and draft —
          and nothing leaves. What I would have sent is held below, so you can see whether I&#8217;m any good before I&#8217;m trusted
          with a stamp. Sending is switched on from our side, not from this page, for now.
        </p>
        <p v-else-if="mode === 'live'" class="sm">
          <strong>Sending is on.</strong> Mail that already sits inside someone&#8217;s own rules goes without waking you.
          <template v-if="envelope && envelope.mandate && envelope.mandate.status === 'active'">Gifts inside <router-link to="/assistant/envelope">the envelope you signed</router-link> go after notice unless you stop them; everything else that costs money waits for your yes.</template>
          <template v-else>Anything that costs money still waits for your yes, every time.</template>
        </p>
        <p v-else-if="isMember === false" class="sm">
          <strong>The desk isn&#8217;t open for you yet.</strong> It runs for members: every morning it reads the people you&#8217;ve added
          and tells you, plainly, who needs something and who doesn&#8217;t.
          <router-link to="/membership">What membership is</router-link>.
        </p>
        <p v-else class="sm">
          <strong>Not running yet.</strong> After its first morning, what it decided — and why — shows up here.
        </p>
      </div>

      <div class="desk">
        <div class="main">

          <!-- 1 · Waiting on you -->
          <div class="divider"><span class="dtab">Waiting on you</span><span class="cnt">{{ waitingCount }} {{ waitingCount === 1 ? 'item' : 'items' }}</span></div>

          <p v-if="!waitingCount" class="sm mut empty">
            Nothing. {{ people.length ? `I read all ${people.length === 1 ? 'one' : people.length} of your people` : 'I read everyone' }} and I&#8217;ll say so plainly
            rather than invent an errand.
          </p>

          <router-link v-for="p in pendingProposals" :key="p.id" :to="`/gifts/${p.id}`" class="prop">
            <span class="lab" :class="{ red: p.autonomyState === 'noticed' }">{{ p.autonomyState === 'noticed' ? 'Unless you stop me' : p.kind === 'gesture' ? 'Something to do yourself' : 'Gift ideas' }}<template v-if="p.occasion"> · {{ p.occasion }}</template></span>
            <h3 class="d3 prop-title">For {{ p.recipientName || 'someone' }}.</h3>
            <p class="sm">
              <template v-if="p.autonomyState === 'noticed' && p.autonomy">{{ p.autonomy.title }}, ordered {{ formatWhen(p.autonomy.ordersAt) }} under your envelope. Nothing is charged until then.</template>
              <template v-else-if="p.kind === 'gesture'">{{ firstTitle(p) }}</template>
              <template v-else>{{ ideaCount(p) }} idea{{ ideaCount(p) === 1 ? '' : 's' }}<template v-if="priceRange(p)"> · <span class="money">{{ priceRange(p) }}</span></template>. Nothing is bought until you say yes.</template>
            </p>
            <span class="btn ghost go">{{ p.autonomyState === 'noticed' ? 'Stop it, or let it go →' : `Look at ${p.kind === 'gesture' ? 'it' : 'them'} →` }}</span>
          </router-link>

          <!-- Going in together (S11): an offer is the member's to answer, never the agent's -->
          <div v-for="p in poolOffers" :id="`pool-${p.pool.poolId}`" :key="`pool-${p.id}`" class="panel pool" :class="{ focus: focusPool === p.pool.poolId }">
            <span class="lab">Going in together? · {{ occasionWord(p.pool) }} · lapses {{ formatDate(p.pool.expiresAt) }}</span>
            <p class="q-text">Someone else is getting {{ firstOf(p.recipientName) }} something for their {{ occasionWord(p.pool) }} too. Want to go in together?</p>
            <p class="sm">I won&#8217;t say who unless you both say yes &mdash; and if you say no, they will never know I asked. A yes tells you each other&#8217;s first name, as each of you puts it, and nothing to reach each other by. You keep the gift you&#8217;re choosing either way, and {{ firstOf(p.recipientName) }} never hears about any of this.</p>
            <form class="pool-form" @submit.prevent>
              <label class="fl" :for="`appear-${p.id}`">They&#8217;d know you as</label>
              <input :id="`appear-${p.id}`" v-model="poolNames[p.pool.poolId]" type="text" maxlength="40" :placeholder="pooling.appearAs || 'Your first name'" :disabled="sending" />
              <div class="btnrow gap">
                <button type="button" class="btn" :disabled="sending" @click="answerPool(p, 'yes')">Yes, go in together</button>
                <button type="button" class="btn ghost" :disabled="sending" @click="answerPool(p, 'no')">No thanks</button>
              </div>
            </form>
          </div>
          <div v-for="p in poolsFormed" :id="`pool-${p.pool.poolId}`" :key="`formed-${p.id}`" class="panel pool formed" :class="{ focus: focusPool === p.pool.poolId }">
            <span class="lab">Going in together · {{ occasionWord(p.pool) }}<template v-if="joint(p).decideBy && joint(p).status === 'ready'"> · choose by {{ formatDate(joint(p).decideBy) }}</template></span>
            <p class="sm"><strong>You and {{ p.pool.with }} are both getting {{ firstOf(p.recipientName) }} something.</strong> That name is all either of you has been told.</p>

            <!-- The joint gift (S12): their two pictures of the person, one slate, both choose or nothing is bought -->
            <p v-if="joint(p).status === 'building'" class="sm muted">I&#8217;m putting what you each know about {{ firstOf(p.recipientName) }} together to look for something you could give together. Neither of you sees the other&#8217;s notes.</p>
            <template v-else-if="joint(p).status === 'ready'">
              <p v-if="joint(p).sharedLabels && joint(p).sharedLabels.length" class="both">You both noticed <strong>{{ joint(p).sharedLabels.slice(0, 2).join(' and ') }}</strong> &mdash; without knowing the other had.</p>
              <p class="sm">Something you could give together, for up to {{ money(joint(p).budgetCents) }} between you. Your share is split by what you each meant to spend ({{ money(joint(p).myBudgetCents) }}), never more.</p>
              <ul class="joint-opts">
                <li v-for="o in joint(p).options" :key="o.candidateId" class="joint-opt" :class="{ mine: joint(p).myVote === o.candidateId }">
                  <img v-if="o.imageUrl" :src="o.imageUrl" alt="" loading="lazy" />
                  <div class="jo-body">
                    <strong>{{ o.title }}</strong>
                    <span class="sm">{{ money(o.priceCents) }} &middot; your share {{ money(o.myShareCents) }}</span>
                    <span v-if="o.bothNoticed.length" class="tag">you both noticed {{ o.bothNoticed[0] }}</span>
                    <span v-else-if="o.beyondEither" class="tag">more than either of you alone</span>
                    <span v-if="o.rationale" class="sm muted">{{ o.rationale }}</span>
                    <span v-if="joint(p).theirVote === o.candidateId" class="sm"><strong>{{ p.pool.with }} chose this one.</strong></span>
                  </div>
                  <button type="button" class="btn small" :class="{ ghost: joint(p).myVote !== o.candidateId }" :disabled="sending || joint(p).myVote === o.candidateId" @click="pickJoint(p, o)">
                    {{ joint(p).myVote === o.candidateId ? 'Your choice' : (joint(p).theirVote === o.candidateId ? 'Yes, this one' : 'This one') }}
                  </button>
                </li>
              </ul>
              <p v-if="joint(p).mine" class="sm">Or your own: <strong>{{ joint(p).mine.title }}</strong> ({{ money(joint(p).mine.priceCents) }}), still waiting for you exactly as it was.</p>
              <p v-if="joint(p).separatelyLikely" class="sm muted">Honestly, nothing here is something either of you couldn&#8217;t give alone. Separately may be better.</p>
              <p class="sm muted">Nothing is bought unless you both choose the same one.</p>
            </template>
            <p v-else-if="['agreed', 'ordering'].includes(joint(p).status)" class="sm"><strong>You both chose {{ chosenTitle(p) }}.</strong> I&#8217;m ordering it now, with your share on your card.</p>
            <p v-else-if="joint(p).status === 'ordered'" class="sm"><strong>Ordered: {{ chosenTitle(p) }}</strong>, from you and {{ p.pool.with }}. Your share was {{ money(joint(p).myChargeCents) }}.</p>
            <p v-else-if="joint(p).status === 'none'" class="sm muted">{{ JOINT_NONE[joint(p).reason] || JOINT_NONE.nothing_better }}</p>
            <p v-else class="sm muted">You each keep your own gift; I&#8217;ll make sure they aren&#8217;t the same kind of thing.</p>

            <div v-if="!['agreed', 'ordering', 'ordered'].includes(joint(p).status)" class="btnrow gap">
              <button v-if="joint(p).status === 'ready'" type="button" class="btn ghost small" :disabled="sending" @click="splitJoint(p)">Separately was better</button>
              <button type="button" class="btn ghost small" :disabled="sending" @click="stepOut(p)">Step out</button>
            </div>
          </div>

          <div v-for="q in openQuestions" :key="q.id" class="panel question">
            <span class="lab">A question<template v-if="personName(q.relationshipId)"> · about {{ personName(q.relationshipId) }}</template> · {{ formatDate(q.at) }}</span>
            <p class="q-text">{{ q.text }}</p>
            <!-- The weekly question: one tap answers it; a sentence is optional -->
            <form v-if="isCheckIn(q)" class="checkin" @submit.prevent>
              <div class="chiprow">
                <button v-for="b in bucketsFor(q)" :key="b.key" type="button" class="chip" :disabled="sending" @click="answerCheckInQ(q, b.key)">{{ b.label }}</button>
              </div>
              <p v-if="isLandingQ(q)" class="xs">I&#8217;d rather know what landed than count what went out. If they told us themselves, it&#8217;s on <router-link :to="`/gifts/${q.refs.proposalId}`">the gift&#8217;s page</router-link>.</p>
              <label class="sr" :for="`q-${q.id}`">Anything to add (optional)</label>
              <input :id="`q-${q.id}`" v-model="questionReplies[q.id]" type="text" maxlength="400" placeholder="Anything to add? Optional — say it before you tap." :disabled="sending" />
            </form>
            <form v-else class="reply-inline" @submit.prevent="sendReply(q)">
              <label class="sr" :for="`q-${q.id}`">Your answer</label>
              <input :id="`q-${q.id}`" v-model="questionReplies[q.id]" type="text" maxlength="600" placeholder="A sentence is plenty." :disabled="sending" />
              <button class="btn" type="submit" :disabled="sending || !(questionReplies[q.id] || '').trim()">Answer</button>
            </form>
          </div>

          <!-- 2 · Shadow: what it would have done (the held rail) -->
          <template v-if="heldAtCounter.length">
            <div class="divider gapped"><span class="dtab">Held at the counter</span><span class="cnt">{{ heldAtCounter.length }} · last 7 days · nothing left</span></div>
            <div v-for="e in heldAtCounter" :key="e.id" class="panel held">
              <div class="held-head">
                <span class="lab">{{ e.kind === 'would_propose' ? 'Would have put gift ideas in front of you' : 'Would have sent' }}<template v-if="personName(e.relationshipId)"> · {{ personName(e.relationshipId) }}</template> · {{ formatDate(e.at) }}</span>
                <span class="rstamp held small">Held</span>
              </div>
              <p class="sm">{{ e.text }}</p>
            </div>
          </template>

          <!-- S13 · Its own holds, and whether you agree: the third leg of "did it land" -->
          <template v-if="recentHolds.length">
            <div class="divider gapped"><span class="dtab">Held back, on purpose</span><span class="cnt">{{ recentHolds.length }} · last 30 days</span></div>
            <div v-for="e in recentHolds" :key="e.id" class="panel hold">
              <span class="lab">{{ personName(e.relationshipId) || 'Someone' }} · {{ formatDate(e.at) }}</span>
              <p class="sm">{{ e.text }}</p>
              <p v-if="e.rating" class="xs">You said: {{ e.rating.verdict === 'right' ? 'the right call' : 'the wrong call' }}<template v-if="e.rating.note"> &mdash; &#8220;{{ e.rating.note }}&#8221;</template>.</p>
              <form v-else class="rate" @submit.prevent>
                <label class="sr" :for="`hold-${e.id}`">Anything to add (optional)</label>
                <input :id="`hold-${e.id}`" v-model="holdNotes[e.id]" type="text" maxlength="400" placeholder="Optional: why? Say it before you tap." :disabled="sending" />
                <div class="btnrow gap">
                  <button type="button" class="btn ghost small" :disabled="sending" @click="rate(e, 'right')">Right call</button>
                  <button type="button" class="btn ghost small" :disabled="sending" @click="rate(e, 'wrong')">I&#8217;d have sent something</button>
                </div>
              </form>
            </div>
          </template>

          <!-- 3 · Their side said no -->
          <template v-if="theirRules.length">
            <div class="divider gapped"><span class="dtab">Held by their rules</span><span class="cnt">{{ theirRules.length }} · no action needed</span></div>
            <div v-for="e in theirRules" :key="e.id" class="panel">
              <div class="held-head">
                <span class="lab">{{ personName(e.relationshipId) || 'Someone' }} · {{ formatDate(e.at) }}</span>
                <span class="rstamp returned">Held — their rules</span>
              </div>
              <p class="sm">{{ e.text }}</p>
              <p class="xs">Their rules, not mine, and I don&#8217;t get to argue with them. Nothing was printed and nothing was charged.</p>
            </div>
          </template>

          <!-- 4 · The board: everyone, and the honest state of each -->
          <div class="divider gapped"><span class="dtab">The board</span><span class="cnt">{{ people.length }} {{ people.length === 1 ? 'person' : 'people' }}</span></div>
          <p v-if="!people.length" class="sm mut empty">Nobody on the board yet. <router-link to="/my-people">Add the people you&#8217;d hate to drift from</router-link>.</p>
          <div v-else class="board">
            <div v-for="b in board" :key="b.id" class="panel person">
              <div class="person-head">
                <h3 class="d4"><router-link :to="`/people/${b.id}`" class="who-link">{{ b.name }}</router-link></h3>
                <span class="pill" :class="b.pillCls">{{ b.pill }}</span>
              </div>
              <p v-if="b.plan && b.plan.intent" class="sm">{{ b.plan.intent }}</p>
              <p v-else class="sm mut">No plan yet. I write one once I have something to go on.</p>
              <p v-if="b.holding" class="xs">Holding back{{ b.plan.hold.until ? ` until ${formatDate(Date.parse(b.plan.hold.until))}` : '' }}: {{ b.plan.hold.why }}</p>
              <p v-else-if="b.plan && b.plan.waitingOn" class="xs">Waiting on: {{ b.plan.waitingOn }}</p>
              <p v-if="b.plan && b.plan.nextTouchAt" class="xs">Next look: {{ formatDate(Date.parse(b.plan.nextTouchAt)) }}<template v-if="b.plan.channel && b.plan.channel !== 'none'"> · {{ channelLabel(b.plan.channel) }}</template></p>
            </div>
          </div>

          <!-- 5 · The record -->
          <details class="drawer gapped">
            <summary>The whole journal · {{ entries.length }} entries</summary>
            <ul class="ev">
              <li v-for="e in entries" :key="e.id">
                <span><span class="lab inl">{{ kindLabel(e.kind) }}<template v-if="personName(e.relationshipId)"> · {{ personName(e.relationshipId) }}</template></span>{{ e.text }}</span>
                <time>{{ formatDate(e.at) }}</time>
              </li>
            </ul>
          </details>
          <details v-if="memory" class="drawer">
            <summary>What I remember about you</summary>
            <pre class="memory">{{ memory }}</pre>
          </details>
        </div>

        <aside class="rail-col">
          <div class="panel">
            <span class="lab">Tell me something</span>
            <form @submit.prevent="sendReply()">
              <label class="fl tell" for="jot">Anything you&#8217;ve heard, or anything I&#8217;ve got wrong. A sentence is plenty.</label>
              <textarea id="jot" v-model="replyText" rows="3" maxlength="600" placeholder="Sam&#8217;s knee is wrecked and he&#8217;s pretending it isn&#8217;t." :disabled="sending"></textarea>
              <label class="sr" for="jot-who">About</label>
              <select id="jot-who" v-model="replyPersonId" :disabled="sending" class="who">
                <option value="">About no one in particular</option>
                <option v-for="p in people" :key="p.id" :value="p.id">About {{ p.name }}</option>
              </select>
              <div class="btnrow gap">
                <button class="btn ghost" type="submit" :disabled="sending || replyText.trim().length < 2">{{ sending ? 'Filing…' : 'File it' }}</button>
              </div>
              <p class="xs gap">I read it before my next morning. Name someone and it goes on their page too.</p>
              <p v-if="jotAddress" class="xs gap jot-mail">
                Or email it, or forward anything, from your account&#8217;s address to
                <button type="button" class="addr" :title="'Copy ' + jotAddress" @click="copyJotAddress">{{ jotAddress }}</button>
              </p>
            </form>
          </div>

          <!-- S13 · The measure: what landed, not what went out -->
          <div v-if="score" class="panel sober landed">
            <span class="lab">What landed · this year</span>
            <p v-if="!score.went && !score.heldBack" class="sm gap">Nothing yet. When something goes out I&#8217;ll ask how it landed &mdash; that, not the count, is the score.</p>
            <template v-else>
              <dl class="money-list">
                <div class="kv"><dt>Went out</dt><dd>{{ score.went }}<template v-if="score.wouldHave"> <span class="mut xs">(+{{ score.wouldHave }} held at the counter)</span></template></dd></div>
                <div class="kv"><dt>Landed</dt><dd>{{ score.loved + score.liked }}<template v-if="score.loved"> <span class="mut xs">· {{ score.loved }} loved</span></template></dd></div>
                <div class="kv"><dt>Missed</dt><dd>{{ score.missed }}</dd></div>
                <div class="kv"><dt>No word yet</dt><dd>{{ score.noWord }}</dd></div>
                <div v-if="score.showedUp" class="kv"><dt>You showed up</dt><dd>{{ score.showedUp }}</dd></div>
                <div class="kv"><dt>Held back</dt><dd>{{ score.heldBack }}<template v-if="score.holdsRight + score.holdsWrong"> <span class="mut xs">· you agreed {{ score.holdsRight }} of {{ score.holdsRight + score.holdsWrong }}</span></template></dd></div>
              </dl>
              <p class="xs gap">{{ scoreLine }}</p>
            </template>
          </div>

          <!-- S17 · the envelope: autonomy, earned and signed -->
          <div v-if="envelope && envelope.available" class="panel envelope">
            <span class="lab" :class="{ red: envelope.mandate && envelope.mandate.status === 'suspended' }">The envelope</span>
            <template v-if="envelope.mandate && envelope.mandate.status === 'active'">
              <p class="sm gap">&#8220;{{ envelope.mandate.description }}&#8221;</p>
              <p class="xs gap">{{ dollars(envelope.mandate.spentCents) }} of {{ dollars(envelope.mandate.envelope.budgetCents) }} used this quarter.</p>
            </template>
            <p v-else-if="envelope.mandate && envelope.mandate.status === 'suspended'" class="sm gap">Suspended: something I chose on my own missed. Nothing more goes out under it.</p>
            <p v-else-if="envelope.gate.earned" class="sm gap">I&#8217;ve held back more than I&#8217;ve sent and you&#8217;ve mostly agreed. If you like, you can let me act alone &mdash; inside limits you sign.</p>
            <p v-else class="sm gap">Not earned yet: {{ envelope.gate.checks.filter((c) => c.ok).length }} of {{ envelope.gate.checks.length }}. When it is, you can let me order a gift on my own, inside limits you sign.</p>
            <div class="btnrow gap"><router-link to="/assistant/envelope" class="btn ghost small">{{ envelope.mandate ? 'Open the envelope →' : 'What it would take →' }}</router-link></div>
          </div>

          <EvidenceSourcePanel v-if="uid" :uid="uid" />

          <div class="panel">
            <span class="lab">Going in together</span>
            <p class="sm gap">If someone else is getting one of your people something for the same birthday, I can put you two together &mdash; blind, and only if you both say yes. The person you&#8217;re both buying for never hears about it.</p>
            <fieldset class="pool-set" :disabled="savingPooling">
              <legend class="sr">Going in together</legend>
              <label v-for="o in POOLING_OPTIONS" :key="o.key" class="opt">
                <input v-model="poolingDraft.setting" type="radio" name="pooling" :value="o.key" />
                <span><strong>{{ o.label }}</strong> <span class="xs">{{ o.hint }}</span></span>
              </label>
              <template v-if="poolingDraft.setting !== 'off'">
                <label class="fl gap" for="appear-as">If it happens, they&#8217;d know you as</label>
                <input id="appear-as" v-model="poolingDraft.appearAs" type="text" maxlength="40" placeholder="Your first name, or &#8220;Sam from climbing&#8221;" />
              </template>
              <div class="btnrow gap">
                <button type="button" class="btn ghost small" :disabled="savingPooling || !poolingChanged" @click="savePooling">{{ savingPooling ? 'Saving…' : 'Save' }}</button>
              </div>
            </fieldset>
          </div>

          <div class="panel sober">
            <span class="lab">This month, on you</span>
            <dl class="money-list">
              <div v-if="isMember" class="kv"><dt>Membership</dt><dd class="money">$12.00</dd></div>
              <div v-if="isMember" class="kv"><dt class="mut">includes</dt><dd class="mut xs">the reading, drafting and post</dd></div>
              <div class="kv"><dt>Gifts approved</dt><dd class="money">{{ dollars(giftsThisMonthCents) }}</dd></div>
              <div class="kv"><dt>Waiting on your yes</dt><dd>{{ pendingGifts.length }}<template v-if="pendingRange"> · <span class="money">{{ pendingRange }}</span></template></dd></div>
            </dl>
            <p class="xs gap">Nothing waiting on your yes has been ordered, made or charged.</p>
          </div>

          <div class="panel folder">
            <span class="lab">Your people</span>
            <p class="sm gap">Add someone, fix an address, or tell me how you two actually keep in touch.</p>
            <div class="btnrow gap"><router-link to="/my-people" class="btn ghost">Open the board →</router-link></div>
          </div>
        </aside>
      </div>
    </template>
  </main>
</template>

<script>
import { db, auth } from '@/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, orderBy, limit, onSnapshot, doc, getDocs, getDoc } from 'firebase/firestore';
import { replyToAgent, getSubscriptionDetails, getJotAddress, answerCheckIn, answerPoolOffer, leavePool, setPoolingPreference, decideJointGift, rateHold, getLandingScore, getEnvelope } from '@/api';
import { isActiveMembershipStatus } from '@/config/membership';
import EvidenceSourcePanel from './EvidenceSourcePanel.vue';

const KIND_LABEL = {
  summary: 'Summary', decision: 'Decision', observation: 'Noticed', question: 'Question for you', member_reply: 'You said',
  suggestion: 'Someone to add?', would_send: 'Would have sent', would_propose: 'Would have proposed', action: 'Sent',
  proposal: 'Proposed a gift', gesture: 'Something for you to do', refusal: 'Their side said no', restraint: 'Holding back',
  quiet: 'A quiet day, on purpose', memory: 'Updated its memory', run_end: 'Run finished', error: 'Something went wrong', note: 'Note',
  pool_offer: 'Going in together?', pool: 'Going in together',
};
// Mirrors POOLING_SETTINGS in functions/shared/recipient/pooling.js.
const POOLING_OPTIONS = [
  { key: 'ask', label: 'Ask me', hint: 'if someone else is open to it. Nobody learns about me unless I say yes.' },
  { key: 'open', label: 'I\u2019m open to it', hint: 'someone else getting them something may be asked about me (not told who) and their yes is enough.' },
  { key: 'off', label: 'Never', hint: 'don\u2019t ask me, and don\u2019t ask anyone about me.' },
];
// A member's own gift still standing: a pool hangs off one of these.
const LIVE_GIFT = new Set(['pending_approval', 'approved', 'purchasing', 'purchased', 'purchase_failed', 'pooled']);
// Why there is no joint gift (functions/shared/recipient/jointGift.js).
const JOINT_NONE = {
  nothing_better: 'I looked for something you could give together and nothing beat what you each had. Separately is better; you each keep your own.',
  nothing_orderable: 'What I found together isn\u2019t something I can order for two people yet. You each keep your own gift.',
  already_chosen: 'One of you went ahead with your own gift, so there won\u2019t be one from you both. You still each know the other is giving something.',
  no_brief: 'I couldn\u2019t put a joint picture together this time. You each keep your own gift.',
  failed: 'I couldn\u2019t put a joint picture together this time. You each keep your own gift.',
};
const CHANNEL_LABEL = { email: 'a note by email', physical: 'something in the post', gift: 'a gift', none: 'just watching' };
const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
const DAY = 24 * 60 * 60 * 1000;
// Mirrors BUCKETS in functions/backend/agent/checkin/checkIn.js.
const CHECKIN_BUCKETS = [
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'months', label: 'A few months ago' },
  { key: 'longer', label: 'Longer than I\u2019d like' },
  { key: 'unsure', label: 'I honestly can\u2019t remember' },
];
// Mirrors LANDING_BUCKETS in functions/backend/agent/landing/landing.js.
const LANDING_BUCKETS = [
  { key: 'loved', label: 'They loved it' },
  { key: 'liked', label: 'It went down fine' },
  { key: 'missed', label: 'Honestly, it missed' },
  { key: 'unheard', label: 'I haven\u2019t heard' },
];
const fmtCents = (c) => `$${(c / 100).toFixed(2).replace(/\.00$/, '')}`;

export default {
  name: 'AssistantPage',
  components: { EvidenceSourcePanel },
  data() {
    return {
      loading: true,
      uid: null,
      entries: [],
      plans: [],
      people: [],
      proposals: [],
      memory: '',
      firstEntryAt: null,
      isMember: null,
      replyText: '',
      jotAddress: '',
      CHECKIN_BUCKETS,
      score: null,
      envelope: null,
      holdNotes: {},
      JOINT_NONE,
      POOLING_OPTIONS,
      pooling: { setting: 'ask', appearAs: '' },
      poolingDraft: { setting: 'ask', appearAs: '' },
      savingPooling: false,
      poolNames: {},
      focusPool: null,
      replyPersonId: '',
      questionReplies: {},
      sending: false,
      authUnsub: null,
      unsubs: [],
    };
  },
  computed: {
    mode() {
      const last = this.entries.find((e) => e.kind === 'run_end' || e.kind === 'summary' || e.kind === 'quiet');
      return last && last.mode ? last.mode : null;
    },
    // Its own words at the end of the newest run: what it did, or why it did nothing.
    latest() {
      return this.entries.find((e) => e.kind === 'summary' || e.kind === 'quiet') || null;
    },
    todayLabel() {
      const d = new Date();
      return `${d.toLocaleDateString('en-US', { weekday: 'long' })} ${d.getDate()} ${d.toLocaleDateString('en-US', { month: 'long' })}`;
    },
    // "Day N of writing nothing" is only said while it is true.
    neverSent() {
      return !this.entries.some((e) => e.kind === 'action' || e.kind === 'proposal');
    },
    daysRunning() {
      return this.firstEntryAt ? Math.max(1, Math.floor((Date.now() - this.firstEntryAt) / DAY) + 1) : 1;
    },
    pendingProposals() {
      return this.proposals.filter((p) => p.status === 'pending_approval').sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    },
    pendingGifts() {
      return this.pendingProposals.filter((p) => p.kind !== 'gesture');
    },
    peopleById() {
      return new Map(this.people.map((p) => [p.id, p.name]));
    },
    // Open questions: answered by a reply that references them, or by any
    // later reply about the same person.
    openQuestions() {
      const replies = this.entries.filter((e) => e.kind === 'member_reply');
      const answeredIds = new Set(replies.filter((e) => e.refs && e.refs.inReplyTo).map((e) => e.refs.inReplyTo));
      // The weekly question closes only when it's actually answered, and an
      // ignored one quietly retires after two weeks (as the server does).
      return this.entries
        .filter((q) => q.kind === 'question' && !answeredIds.has(q.id) && !this.landingAnswered(q) &&
          (this.isCheckIn(q)
            ? q.at > Date.now() - 14 * DAY
            : !(q.relationshipId && replies.some((r) => r.relationshipId === q.relationshipId && r.at > q.at))))
        .slice(0, 5);
    },
    poolOffers() {
      const now = Date.now();
      return this.proposals.filter((p) => p.pool && p.pool.state === 'offered' && p.pool.role === 'offeree' && p.pool.expiresAt > now);
    },
    poolsFormed() {
      // Until the day itself; after that it's history, and on the person's page.
      return this.proposals.filter((p) => p.pool && p.pool.state === 'formed' && LIVE_GIFT.has(p.status) &&
        (!p.pool.occasion || p.pool.occasion.at > Date.now() - DAY));
    },
    poolingChanged() {
      return this.poolingDraft.setting !== this.pooling.setting || (this.poolingDraft.appearAs || '') !== (this.pooling.appearAs || '');
    },
    waitingCount() {
      return this.pendingProposals.length + this.openQuestions.length + this.poolOffers.length;
    },
    headline() {
      if (this.mode === null && this.isMember === false) return 'Your desk, before it opens.';
      const n = this.waitingCount;
      if (n === 0) return 'Nothing needs you today.';
      if (n === 1) return 'One thing, then you’re done.';
      return `${NUMBER_WORDS[n] || n} things, then you’re done.`;
    },
    // Shadow mode: what it would have done this week, held.
    heldAtCounter() {
      const since = Date.now() - 7 * DAY;
      return this.entries.filter((e) => (e.kind === 'would_send' || e.kind === 'would_propose') && e.at >= since);
    },
    recentHolds() {
      const since = Date.now() - 30 * DAY;
      return this.entries.filter((e) => e.kind === 'restraint' && e.at >= since).slice(0, 6);
    },
    scoreLine() {
      const s = this.score;
      if (!s) return '';
      const landed = s.loved + s.liked;
      if (s.heldBack > s.went) return `I held back more often than I sent${s.holdsRight + s.holdsWrong ? `, and you agreed with ${s.holdsRight} of the ${s.holdsRight + s.holdsWrong} you rated` : ''}. That\u2019s the job working.`;
      if (s.missed > landed) return 'More missed than landed. I\u2019m reading the misses as evidence, not bad luck, and sending less.';
      if (s.noWord > landed + s.missed) return 'Mostly no word yet. Each week I ask about one; a tap is enough.';
      return 'What landed is what I learn from. The count of what went out isn\u2019t the point.';
    },
    theirRules() {
      const since = Date.now() - 30 * DAY;
      return this.entries.filter((e) => e.kind === 'refusal' && e.at >= since);
    },
    board() {
      const byRel = new Map(this.plans.map((p) => [p.relationshipId, p]));
      return this.people.map((person) => {
        const plan = byRel.get(person.id) || null;
        const holding = this.holdingBack(plan);
        const waiting = this.pendingProposals.some((p) => p.relationshipId === person.id);
        // "Nothing owed" is a judgement; without a plan there isn't one yet.
        let pill = plan ? 'Nothing owed' : 'No plan yet';
        let pillCls = plan ? 'ok' : '';
        if (waiting) { pill = 'Waiting on you'; pillCls = 'no'; } else if (holding) { pill = 'Holding back'; pillCls = ''; } else if (plan && plan.channel && plan.channel !== 'none') { pill = 'Planned'; pillCls = ''; }
        return { ...person, plan, holding, pill, pillCls };
      });
    },
    giftsThisMonthCents() {
      const start = new Date(); start.setDate(1); start.setHours(0, 0, 0, 0);
      return this.proposals
        .filter((p) => ['purchasing', 'purchased'].includes(p.status) && p.billing && typeof p.billing.amountCents === 'number' &&
          ((p.decision && p.decision.decidedAt) || p.updatedAt || 0) >= start.getTime())
        .reduce((sum, p) => sum + p.billing.amountCents, 0);
    },
    pendingRange() {
      const prices = this.pendingGifts.flatMap((p) => (p.proposals || []).map((i) => i.priceCents)).filter((c) => typeof c === 'number');
      if (!prices.length) return '';
      const lo = Math.min(...prices); const hi = Math.max(...prices);
      return lo === hi ? fmtCents(lo) : `${fmtCents(lo)}–${fmtCents(hi)}`;
    },
  },
  created() {
    this.authUnsub = onAuthStateChanged(auth, (user) => {
      this.teardown();
      this.uid = user ? user.uid : null;
      if (!user) {
        this.entries = []; this.plans = []; this.people = []; this.proposals = []; this.memory = '';
        this.loading = false;
        return;
      }
      this.subscribe(user.uid);
      this.loadMembership();
      this.loadJotAddress();
      this.answerFromLink();
      this.loadPooling(user.uid);
      this.focusPoolFromLink();
      this.loadScore();
      this.loadEnvelope();
    });
  },
  beforeUnmount() {
    this.teardown();
    if (this.authUnsub) this.authUnsub();
  },
  methods: {
    // Mirrors holdActive() in the agent's prompt.js: an open-ended hold stands for a week.
    holdingBack(plan) {
      const h = plan && plan.hold;
      if (!h || !h.at) return false;
      if (h.until) return Date.parse(`${h.until}T23:59:59Z`) >= Date.now();
      return h.at + 7 * DAY > Date.now();
    },
    subscribe(uid) {
      this.loading = true;
      const fail = (what) => () => this.$toast.error(`Couldn't load ${what}.`);
      const entriesCol = collection(db, 'agentJournal', uid, 'entries');
      this.unsubs.push(onSnapshot(query(entriesCol, orderBy('at', 'desc'), limit(80)), (snap) => {
        this.entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.loading = false;
      }, () => { this.loading = false; fail('the journal')(); }));
      // The very first entry: "day N" is counted from it, honestly.
      getDocs(query(entriesCol, orderBy('at', 'asc'), limit(1)))
        .then((snap) => { this.firstEntryAt = snap.empty ? null : snap.docs[0].data().at; })
        .catch(() => { this.firstEntryAt = null; });
      this.unsubs.push(onSnapshot(query(collection(db, 'agentPlans'), where('userId', '==', uid)), (snap) => {
        this.plans = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }, fail('its plans')));
      // Same set the agent itself considers: active relationships only.
      this.unsubs.push(onSnapshot(query(collection(db, 'relationships'), where('userId', '==', uid), where('status', '==', 'active')), (snap) => {
        this.people = snap.docs.map((d) => ({ id: d.id, name: String(d.data().name || 'Someone') })).sort((a, b) => a.name.localeCompare(b.name));
      }, fail('your people')));
      // where(userId ==) only — no orderBy, so no composite index is needed.
      this.unsubs.push(onSnapshot(query(collection(db, 'giftProposals'), where('userId', '==', uid)), (snap) => {
        this.proposals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }, fail('gift ideas')));
      this.unsubs.push(onSnapshot(doc(db, 'agentMemory', uid), (snap) => {
        this.memory = snap.exists() ? (snap.data().notes || '') : '';
      }, fail('its memory')));
    },
    isCheckIn(q) {
      return !!(q && q.refs && (q.refs.checkIn === 'last_talked' || q.refs.checkIn === 'landed'));
    },
    isLandingQ(q) {
      return !!(q && q.refs && q.refs.checkIn === 'landed');
    },
    bucketsFor(q) {
      return this.isLandingQ(q) ? LANDING_BUCKETS : CHECKIN_BUCKETS;
    },
    // Answered on the gift's own page instead.
    landingAnswered(q) {
      if (!this.isLandingQ(q)) return false;
      const p = this.proposals.find((x) => x.id === q.refs.proposalId);
      return !!(p && p.landing && p.landing.member && p.landing.member.at > q.at);
    },
    async loadEnvelope() {
      try {
        const res = await getEnvelope();
        this.envelope = (res && res.data) || null;
      } catch (e) {
        this.envelope = null;
      }
    },
    formatWhen(ms) {
      return ms ? new Date(ms).toLocaleString(undefined, { weekday: 'long', hour: 'numeric', minute: '2-digit' }) : '';
    },
    async loadScore() {
      try {
        const res = await getLandingScore();
        this.score = (res && res.data) || null;
      } catch (e) {
        this.score = null;
      }
    },
    async rate(e, verdict) {
      if (this.sending) return;
      this.sending = true;
      try {
        const note = (this.holdNotes[e.id] || '').trim();
        await rateHold({ entryId: e.id, verdict, ...(note ? { note } : {}) });
        this.holdNotes = { ...this.holdNotes, [e.id]: '' };
        this.$toast.success(verdict === 'right' ? 'Noted. That\u2019s the restraint working.' : 'Noted, and passed on. I read it before my next morning.');
        this.loadScore();
      } catch (err) {
        this.$toast.error((err && err.message) || 'Couldn\u2019t save that.');
      } finally {
        this.sending = false;
      }
    },
    async answerCheckInQ(q, bucket) {
      if (this.sending) return;
      this.sending = true;
      try {
        const text = q ? (this.questionReplies[q.id] || '').trim() : '';
        const res = await answerCheckIn({ entryId: q ? q.id : this.$route.query.checkin, bucket, ...(text ? { text } : {}) });
        const d = (res && res.data) || {};
        if (q) this.questionReplies = { ...this.questionReplies, [q.id]: '' };
        const first = String(d.name || '').split(' ')[0] || 'them';
        if (d.kind === 'landed') {
          this.$toast.success(d.already ? 'You already answered that one.'
            : bucket === 'unheard' ? 'Fair enough. You can tell me later on the gift\u2019s page.'
              : `Thanks. That\u2019s worth more to me than the order was.`);
          this.loadScore();
          return;
        }
        this.$toast.success(d.already
          ? `You already answered that one. It\u2019s on ${first}\u2019s page.`
          : bucket === 'unsure'
            ? `Noted, honestly. That goes on ${first}\u2019s page too.`
            : `On ${first}\u2019s page. Thanks \u2014 that\u2019s the only question this week.`);
      } catch (e) {
        this.$toast.error((e && e.message) || 'Couldn\u2019t file that.');
      } finally {
        this.sending = false;
      }
    },
    // The weekly email's buttons land here: /assistant?checkin=<id>&a=<bucket>.
    async answerFromLink() {
      const { checkin, a } = this.$route.query || {};
      if (!checkin || !a || ![...CHECKIN_BUCKETS, ...LANDING_BUCKETS].some((b) => b.key === a)) return;
      this.$router.replace({ query: {} }).catch(() => {});
      await this.answerCheckInQ(null, String(a));
    },
    firstOf(name) {
      return String(name || '').trim().split(/\s+/)[0] || 'them';
    },
    occasionWord(pool) {
      return (pool && pool.occasion && pool.occasion.type) || 'occasion';
    },
    async loadPooling(uid) {
      try {
        const snap = await getDoc(doc(db, 'users', uid));
        const p = (snap.exists() && snap.data().pooling) || {};
        this.pooling = { setting: p.setting || 'ask', appearAs: p.appearAs || '' };
        this.poolingDraft = { ...this.pooling };
      } catch (e) {
        // Unknown: show the default; saving writes through the server anyway.
      }
    },
    async savePooling() {
      this.savingPooling = true;
      try {
        const res = await setPoolingPreference({ setting: this.poolingDraft.setting, appearAs: (this.poolingDraft.appearAs || '').trim() });
        const d = (res && res.data) || {};
        this.pooling = { setting: d.setting, appearAs: this.poolingDraft.appearAs ? d.appearAs : '' };
        this.poolingDraft = { ...this.pooling };
        this.$toast.success(d.declined
          ? 'Saved. I said no thanks to what was waiting, and they\u2019ll never know I asked.'
          : d.setting === 'off' ? 'Saved. Nobody will be asked about you, and I won\u2019t ask you.' : `Saved. If it happens, they\u2019ll know you as ${d.appearAs}.`);
      } catch (e) {
        this.$toast.error((e && e.message) || 'Couldn\u2019t save that.');
      } finally {
        this.savingPooling = false;
      }
    },
    async answerPool(p, answer) {
      if (this.sending) return;
      this.sending = true;
      try {
        const appearAs = (this.poolNames[p.pool.poolId] || this.pooling.appearAs || '').trim();
        const res = await answerPoolOffer({ poolId: p.pool.poolId, answer, ...(appearAs ? { appearAs } : {}) });
        const d = (res && res.data) || {};
        const first = this.firstOf(p.recipientName);
        if (d.state === 'formed') this.$toast.success(`You and ${d.with} are both getting ${first} something. They know you as ${d.appearAs || 'you put it'}.`);
        else if (d.state === 'declined') this.$toast.success('No thanks, then. They\u2019ll never know I asked.');
        else this.$toast.info('That offer has lapsed. Your own gift is exactly as it was.');
      } catch (e) {
        this.$toast.error((e && e.message) || 'Couldn\u2019t answer that.');
      } finally {
        this.sending = false;
      }
    },
    joint(p) {
      return (p.pool && p.pool.joint) || {};
    },
    money(cents) {
      const n = (cents || 0) / 100;
      return `$${n % 1 ? n.toFixed(2) : n.toFixed(0)}`;
    },
    chosenTitle(p) {
      const j = this.joint(p);
      const o = (j.options || []).find((x) => x.candidateId === j.chosen);
      return o ? o.title : 'it';
    },
    async pickJoint(p, o) {
      if (this.sending) return;
      this.sending = true;
      try {
        const res = await decideJointGift({ poolId: p.pool.poolId, choice: 'pick', candidateId: o.candidateId });
        const d = (res && res.data) || {};
        if (d.agreed) this.$toast.success(`You both chose ${o.title}. Ordering it now.`);
        else this.$toast.success(`Chosen. If ${p.pool.with} chooses it too, I\u2019ll order it.`);
      } catch (e) {
        this.$toast.error((e && e.message) || 'Couldn\u2019t do that.');
      } finally {
        this.sending = false;
      }
    },
    async splitJoint(p) {
      if (this.sending) return;
      this.sending = true;
      try {
        await decideJointGift({ poolId: p.pool.poolId, choice: 'split' });
        this.$toast.success('Separately, then. You\u2019re back to your own gift, which hasn\u2019t changed.');
      } catch (e) {
        this.$toast.error((e && e.message) || 'Couldn\u2019t do that.');
      } finally {
        this.sending = false;
      }
    },
    async stepOut(p) {
      if (this.sending) return;
      this.sending = true;
      try {
        await leavePool({ poolId: p.pool.poolId });
        this.$toast.success('Done. You\u2019re back to your own gift, which hasn\u2019t changed.');
      } catch (e) {
        this.$toast.error((e && e.message) || 'Couldn\u2019t do that.');
      } finally {
        this.sending = false;
      }
    },
    // The offer email's button lands here: /assistant?pool=<id>.
    focusPoolFromLink() {
      const { pool } = this.$route.query || {};
      if (!pool || !/^pool_[a-f0-9]{24}$/.test(String(pool))) return;
      this.focusPool = String(pool);
      this.$router.replace({ query: {} }).catch(() => {});
      setTimeout(() => {
        const el = document.getElementById(`pool-${this.focusPool}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 800);
    },
    async loadJotAddress() {
      try {
        const res = await getJotAddress();
        this.jotAddress = (res && res.data && res.data.enabled && res.data.address) || '';
      } catch (e) {
        this.jotAddress = '';
      }
    },
    async copyJotAddress() {
      try {
        await navigator.clipboard.writeText(this.jotAddress);
        this.$toast.success('Copied. Put it in your contacts as "the desk".');
      } catch (e) {
        this.$toast.info(this.jotAddress);
      }
    },
    async loadMembership() {
      try {
        const res = await getSubscriptionDetails();
        const details = (res && res.data && res.data.subscriptionDetails) || null;
        this.isMember = Boolean(details && isActiveMembershipStatus(details.status));
      } catch (e) {
        this.isMember = null; // unknown: say nothing about money we can't see
      }
    },
    teardown() {
      this.unsubs.forEach((u) => u());
      this.unsubs = [];
    },
    personName(id) {
      return (id && this.peopleById.get(id)) || '';
    },
    kindLabel(kind) {
      return KIND_LABEL[kind] || kind || 'Note';
    },
    channelLabel(channel) {
      return CHANNEL_LABEL[channel] || channel || '';
    },
    ideaCount(p) {
      return (p.proposals || []).length;
    },
    firstTitle(p) {
      return p.proposals && p.proposals[0] ? p.proposals[0].title : '';
    },
    priceRange(p) {
      const prices = (p.proposals || []).map((i) => i.priceCents).filter((c) => typeof c === 'number');
      if (!prices.length) return '';
      const lo = Math.min(...prices); const hi = Math.max(...prices);
      return lo === hi ? fmtCents(lo) : `${fmtCents(lo)}–${fmtCents(hi)}`;
    },
    dollars(cents) {
      return `$${(cents / 100).toFixed(2)}`;
    },
    formatDate(ms) {
      if (!ms) return '';
      return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    },
    async sendReply(question = null) {
      const text = question ? (this.questionReplies[question.id] || '').trim() : this.replyText.trim();
      if (text.length < 2 || this.sending) return;
      this.sending = true;
      try {
        const res = await replyToAgent({
          text,
          inReplyTo: question ? question.id : undefined,
          personId: question ? (question.relationshipId || undefined) : (this.replyPersonId || undefined),
        });
        if (question) this.questionReplies = { ...this.questionReplies, [question.id]: '' };
        else this.replyText = '';
        const filedTo = (res && res.data && res.data.filedTo) || [];
        this.$toast.success(filedTo.length
          ? `Filed on ${filedTo.map((p) => `${p.name}\u2019s`).join(' and ')} page. I read it before my next morning.`
          : 'Filed. I read it before my next morning.');
      } catch (e) {
        this.$toast.error((e && e.message) || "Couldn't file that.");
      } finally {
        this.sending = false;
      }
    },
  },
};
</script>

<style scoped>
.top { display: flex; flex-wrap: wrap; gap: 14px 24px; align-items: center; }
.top .rstamp { margin-left: auto; }
.head { margin-top: 8px; }
.said { margin-top: 14px; max-width: 64ch; }
.said .lede { white-space: pre-line; }
.sig { margin-top: 8px; letter-spacing: .14em; }
.rail { margin-top: 20px; }
.desk { display: grid; gap: clamp(28px, 3.6vw, 48px); margin-top: 34px; }
@media (min-width: 1000px) { .desk { grid-template-columns: minmax(0, 1.5fr) minmax(260px, .75fr); align-items: start; } }
.divider.gapped, .drawer.gapped { margin-top: 44px; }
.gap { margin-top: 12px; }
.empty { margin: 0 0 8px; }

.prop {
  display: block; position: relative; border: 1.5px solid var(--ink); background: var(--sheet);
  padding: clamp(16px, 2.4vw, 26px); color: var(--ink); text-decoration: none; margin-bottom: 16px;
}
.prop:hover { background: var(--sunk); color: var(--ink); }
.prop-title { margin: 8px 0; }
.who-link { color: var(--ink); text-decoration: none; }
.who-link:hover { text-decoration: underline; text-underline-offset: 4px; }
.prop .go { margin-top: 14px; pointer-events: none; }

.question { margin-bottom: 16px; }
.pool { margin-bottom: 16px; }
.pool.focus { outline: 2px solid currentColor; outline-offset: 3px; }
.pool-form input, .pool-set input[type="text"] { width: 100%; box-sizing: border-box; }
.pool-set { border: 0; padding: 0; margin: 10px 0 0; }
.pool-set .opt { display: flex; gap: 8px; align-items: baseline; margin: 6px 0; cursor: pointer; }
.pool .muted { color: var(--ink-2); }
.pool .both { margin: 6px 0 10px; font-family: var(--serif, Georgia, serif); font-size: 1.05rem; }
.joint-opts { list-style: none; margin: 10px 0; padding: 0; }
.joint-opt { display: grid; grid-template-columns: 56px 1fr auto; gap: 12px; align-items: center; padding: 10px 0; border-top: 1px solid var(--rule); }
.joint-opt:not(:has(img)) { grid-template-columns: 1fr auto; }
.joint-opt img { width: 56px; height: 56px; object-fit: cover; border: 1px solid var(--rule); }
.joint-opt.mine { background: var(--sunk); }
.jo-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.jo-body strong { overflow-wrap: anywhere; }
.joint-opt .tag { align-self: flex-start; font-family: var(--mono, monospace); font-size: .72rem; text-transform: uppercase; letter-spacing: .04em; border: 1px solid currentColor; padding: 1px 6px; margin-top: 2px; }
@media (max-width: 480px) {
  .joint-opt { grid-template-columns: 44px 1fr; }
  .joint-opt img { width: 44px; height: 44px; }
  .joint-opt .btn { grid-column: 1 / -1; justify-self: start; }
}
.hold { margin-bottom: 14px; }
.rate input { width: 100%; box-sizing: border-box; margin-top: 8px; }
.landed .kv dd { font-variant-numeric: tabular-nums; }
.landed .kv dd .xs { margin-left: .4em; }
.checkin .chiprow { margin: 10px 0; }
.checkin input { width: 100%; box-sizing: border-box; }
.q-text { margin: 10px 0 12px; }
.reply-inline { display: flex; gap: 10px; flex-wrap: wrap; }
.reply-inline input { flex: 1 1 14rem; width: auto; }

.held { margin-bottom: 14px; }
.held-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; flex-wrap: wrap; margin-bottom: 8px; }
.rstamp.small { font-size: .6rem; padding: 3px 8px; }

.board { display: grid; gap: 18px; }
@media (min-width: 660px) { .board { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.person-head { display: flex; justify-content: space-between; gap: 10px; align-items: baseline; margin-bottom: 8px; }
.person .xs { margin-top: 6px; }

.lab.inl { display: inline; margin-right: 8px; }
.memory { white-space: pre-wrap; font-family: var(--mono); font-size: .8rem; line-height: 1.6; color: var(--ink-2); background: var(--sheet); border: 1px solid var(--rule); padding: 14px; margin: 0 0 18px; }

.rail-col { display: grid; gap: 12px; }
.tell { margin-top: 8px; }
.who { margin-top: 8px; }
.jot-mail .addr { all: unset; cursor: pointer; font-family: var(--mono, monospace); text-decoration: underline dotted; overflow-wrap: anywhere; }
.jot-mail .addr:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
.money-list { margin: 10px 0 0; }
</style>
